/**
 * Business-taxonomy tag chips for a repository row (enterprise-project-
 * organization Phase B1). Dimension-coloured, label-resolved from the tenant
 * taxonomy; renders nothing when the repo carries no tags (dormancy).
 */

import type { TaxonomyDimension } from '@/services/projectsApi';
import { cn } from '@/lib/utils';

// Stable per-dimension chip palette: built-ins get fixed colours, custom
// dimensions rotate through the tail by definition order.
const DIM_STYLES = [
  'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
];

export function dimStyle(dimensions: TaxonomyDimension[], dimKey: string): string {
  const idx = Math.max(0, dimensions.findIndex((d) => d.dimKey === dimKey));
  return DIM_STYLES[idx % DIM_STYLES.length];
}

export function valueLabel(
  dimensions: TaxonomyDimension[],
  dimKey: string,
  valueSlug: string,
): string {
  const dim = dimensions.find((d) => d.dimKey === dimKey);
  return dim?.values.find((v) => v.valueSlug === valueSlug)?.label ?? valueSlug;
}

export function TagChips({
  tags,
  dimensions,
  className,
}: {
  tags: Record<string, string>;
  dimensions: TaxonomyDimension[];
  className?: string;
}) {
  const entries = dimensions
    .filter((d) => tags[d.dimKey])
    .map((d) => ({ dim: d, value: tags[d.dimKey] }));
  if (entries.length === 0) return null;
  return (
    <span className={cn('flex flex-wrap items-center gap-1', className)}>
      {entries.map(({ dim, value }) => (
        <span
          key={dim.dimKey}
          title={`${dim.label}: ${valueLabel(dimensions, dim.dimKey, value)}`}
          className={cn(
            'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium leading-none',
            dimStyle(dimensions, dim.dimKey),
          )}
        >
          {valueLabel(dimensions, dim.dimKey, value)}
        </span>
      ))}
    </span>
  );
}
